import { fullname, getModelTypeLabel } from "~/lib/utils";
import moment from "moment";
import type { INotification } from "~/routes/portal/type";

import { Avatar, AvatarImage, Icon } from "../ui";
import { Checkbox } from "../ui/checkbox";
import { metaFor } from "./notification-item.utils";

export const NotificationRow = ({
  item,
  onClick,
  onToggleRead,
  onDismiss,
  selectable = false,
  selected = false,
  onSelectChange,
}: {
  item: INotification;
  onClick: (item: INotification) => void;
  onToggleRead?: (item: INotification) => void;
  onDismiss?: (item: INotification) => void;
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (item: INotification, selected: boolean) => void;
}) => {
  const meta = metaFor(item);
  // Per DEV_STANDARDS: only render an avatar when a real image URL is present — never an initials
  // badge. Otherwise show a neutral type-icon tile.
  const avatarUrl = item?.createdBy?.avatar?.toString();
  const creatorName = item?.createdBy
    ? fullname(item.createdBy.firstName, item.createdBy.lastName).trim()
    : "";

  return (
    <div
      role="listitem"
      tabIndex={0}
      onClick={() => onClick(item)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(item);
        }
      }}
      className={`group relative flex cursor-pointer gap-3 rounded-lg px-2 py-3 outline-none transition-colors hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-ring ${
        item.isRead ? "bg-white" : "bg-neutral-50"
      }`}
    >
      <span className={`absolute inset-y-2 left-0 w-1 rounded-full ${meta.rail}`} aria-hidden />
      {selectable && (
        <div
          className="flex items-center pl-1"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={selected}
            onCheckedChange={(v) => onSelectChange?.(item, Boolean(v))}
            aria-label="Select notification"
          />
        </div>
      )}
      {(onToggleRead || onDismiss) && (
        <div className="absolute right-2 top-2 z-10 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          {onToggleRead && (
            <button
              type="button"
              title={item.isRead ? "Mark as unread" : "Mark as read"}
              aria-label={item.isRead ? "Mark as unread" : "Mark as read"}
              onClick={(e) => {
                e.stopPropagation();
                onToggleRead(item);
              }}
              className="flex size-7 items-center justify-center rounded-full bg-white text-neutral-500 shadow-xsmall hover:bg-neutral-100 hover:text-neutral-800"
            >
              <Icon name={item.isRead ? "Undo2" : "Check"} className="size-3.5" />
            </button>
          )}
          {onDismiss && (
            <button
              type="button"
              title="Dismiss"
              aria-label="Dismiss notification"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(item);
              }}
              className="flex size-7 items-center justify-center rounded-full bg-white text-neutral-500 shadow-xsmall hover:bg-danger-100 hover:text-danger-600"
            >
              <Icon name="X" className="size-3.5" />
            </button>
          )}
        </div>
      )}
      {avatarUrl ? (
        <Avatar className="ml-1 bg-neutral-100">
          <AvatarImage src={avatarUrl} alt="" />
        </Avatar>
      ) : (
        <div
          className={`ml-1 flex size-10 shrink-0 items-center justify-center rounded-full ${meta.pill}`}
          aria-hidden
        >
          <Icon name={meta.icon as any} className="size-5" />
        </div>
      )}
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-Medium ${meta.pill}`}>
            <Icon name={meta.icon as any} className="size-3.5" />
            {meta.label}
          </span>
          <span className="truncate text-xs text-neutral-400">{getModelTypeLabel(item?.modelType)}</span>
          {!item.isRead && <span className="ml-auto size-2 shrink-0 rounded-full bg-primary-500" aria-label="unread" />}
        </div>
        <div className="rounded-md border border-neutral-200 px-3 py-1.5">
          <p className="truncate text-sm font-Medium normal-case text-neutral-800" title={item?.title}>
            {item?.title}
          </p>
        </div>
        <div className="flex items-center justify-between gap-2 text-xs text-neutral-500">
          <span className="truncate">
            {creatorName ? (
              <>
                <span className="text-neutral-400">Created by </span>
                <span className="text-neutral-600">{creatorName}</span>
              </>
            ) : (
              meta.label
            )}
            {item?.site ? ` · ${item.site}` : ""}
            {item?.dueDate ? ` · Due ${moment(item.dueDate).fromNow()}` : ""}
          </span>
          <span className="shrink-0">{moment(item?.createdAt).fromNow()}</span>
        </div>
      </div>
    </div>
  );
};
